import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Select,
  message,
} from 'antd';
import axios from 'axios';
import PropTypes from 'prop-types';

import BoardColorPicker from './BoardColorPicker';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';

import { getBoardDetails } from './state/boards.actions';

const formLabelStyle = {
  style: {
    paddingBottom: 5,
    marginTop: 10,
  },
};

export default function BoardMoveWorkflowConfigure({
  cardTypeId,
  targetBoardId,
  currentStatuses = [],
  keepColor = false,
  isCopyCard,
}) {
  const dispatch = useDispatch();
  const boards = useSelector((state) => state.boards.boards);
  const selectedBoard = useSelector((state) => state.boards.selectedBoard);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);

  const { id } = selectedBoard;

  const [targetStatuses, setTargetStatuses] = useState([]);

  const statusOptions = useMemo(() => (
    currentStatuses.map((status) => ({ label: status.title, value: status.id }))
  ), [currentStatuses]);

  const boardNames = useMemo(() => (
    Object.values(boards)
      .filter((board) => board.cardTypeId === cardTypeId && board.id !== id)
      .map((board) => ({ value: board.id, label: board.title }))
  ), [boards, cardTypeId, id]);

  const targetBoardColors = useMemo(() => (
    boardDetailsMap[targetBoardId]?.colors
    ?? (targetBoardId === selectedBoard?.id ? selectedBoard?.colors : undefined)
  ), [boards, targetBoardId, selectedBoard]);

  useEffect(() => {
    const getTargetStatuses = async () => {
      try {
        const {
          data: rawTargetStatuses = [],
        } = await axios.get(`/boards/${targetBoardId}/statuses`);
        setTargetStatuses(
          rawTargetStatuses.map((status) => ({ value: status.id, label: status.title })),
        );
        dispatch(getBoardDetails(targetBoardId, false));
      } catch (err) {
        message.error('Could not get target board statuses');
      }
    };
    if (targetBoardId) getTargetStatuses();
  }, [targetBoardId]);

  return (
    <>
      <Form.Item
        name="initialStatusId"
        label="Trigger Status"
        rules={[{ required: true, message: 'Trigger Status is required' }]}
        labelCol={formLabelStyle}
        style={{ marginBottom: 0 }}
      >
        <Select options={statusOptions} />
      </Form.Item>
      {!isCopyCard && (
        <Form.Item
          name="createShadow"
          style={{ marginBottom: 0, marginTop: 10, paddingLeft: 5 }}
        >
          <OnTraccrCheckbox
            label="Create Shadow?"
            hoverNode={(
              <div style={{ maxWidth: 400 }}>
                Check this box if you want a display
                only card shadow left in this board when
                this workflow is triggered.
              </div>
            )}
          />
        </Form.Item>
      )}
      <Form.Item
        name="targetBoardId"
        label="Target Board"
        rules={[{ required: true, message: 'Target Board is required' }]}
        labelCol={formLabelStyle}
        style={{ marginBottom: 0 }}
      >
        <Select options={boardNames} />
      </Form.Item>
      {targetBoardId && (
        <Form.Item
          name="targetStatusId"
          label="Target Status"
          rules={[{ required: true, message: 'Target Status is required' }]}
          labelCol={formLabelStyle}
          style={{ marginBottom: 0 }}
        >
          <Select options={targetStatuses} />
        </Form.Item>
      )}
      {targetBoardId && !keepColor && (
        <Form.Item
          name="color"
          label="Target Color"
          labelCol={formLabelStyle}
          style={{ marginBottom: 0, maxWidth: 250 }}
        >
          <BoardColorPicker isNotDisplay boardColors={targetBoardColors} />
        </Form.Item>
      )}
      {targetBoardId && (
        <Form.Item
          name="keepColor"
          label="Keep Color"
          valuePropName="checked"
          labelCol={formLabelStyle}
          style={{ marginBottom: 0 }}
        >
          <OnTraccrSwitch />
        </Form.Item>
      )}
    </>
  );
}

BoardMoveWorkflowConfigure.propTypes = {
  cardTypeId: PropTypes.number.isRequired,
  targetBoardId: PropTypes.string,
  currentStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
  })),
  keepColor: PropTypes.bool,
  isCopyCard: PropTypes.func,
};

BoardMoveWorkflowConfigure.defaultProps = {
  targetBoardId: null,
  keepColor: false,
  isCopyCard: false,
  currentStatuses: [],
};
